import type { CSSProperties } from 'react';
import { useMemo } from 'react';

import { ThemeProvider } from '@mui/material';
import type { ColDef, RowClassParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue } from 'recoil';

import { useGridScale } from '../../features/grid-settings';
import useDrawer from '../../hooks/useDrawer';
import useIncidents from '../../hooks/useIncidents';
import useLocale from '../../hooks/useLocale';
import makeTheme from '../../makeTheme';
import {
  EntitiesGridBox,
  useCurrentUser,
  useDefaultGridSettings,
  useGetLocaleText,
  useGridFlaggedSetter,
} from '../../modules/EntitiesGrid';
import { defaultCellStyles } from '../../modules/EntitiesGrid/lib';
import { useGridSetters } from '../../modules/Tasks/state/tasks';
import { Drawer } from '../../state/drawer';
import { permissionState } from '../../state/event';
import { useEditIncidentPart } from '../../state/incidents';
import { incidentsFilterState } from '../../state/incidentsFilter';
import { pageState } from '../../state/incidentTable/pagination';
import { userEventSettings, userState } from '../../state/user';
import type { Incident } from '../../types';
import { Status } from '../../types';
import Pagination from './components/Pagination';
import { useColumnState } from './hooks';
import useColumnDefs from './hooks/useColumnDefs';
import { useIncidentInlineAdding } from './hooks/useIncidentInlineAdding';
import { cellСanBeEdited } from './utils/cellCanBeEdited';
import { scrollToColumnEdited } from './utils/scrollToColumnEdited';
import { useScrollToRowByParams } from './utils/useScrollToRowByParams';

import './index.css';

const rowClassRules = {
  'row-draft': (params: RowClassParams<Incident>) => params.data?.status === Status.DRAFT,
};

const CONTAINER_STYLE = { height: 'calc(100% - 53px)' };

const IncidentTable = () => {
  const { data } = useIncidents();
  const { t } = useTranslation();
  const user = useRecoilValue(userState);
  const { currentUserId } = useCurrentUser();
  const userEventSetting = useRecoilValue(userEventSettings);
  const { scrollToRowByParams } = useScrollToRowByParams();
  const { execute: editIncidentPart } = useEditIncidentPart();

  const scale = useGridScale('incident');
  const { ref: gridRef, ...gridDefaultSettings } = useDefaultGridSettings({
    compact: userEventSetting.incidentGridSettings.viewMode === 'compact',
    scale,
  });
  const { canUpdateResolvedIncident, canUpdateIncident, canUpdateAllIncidents, canUpdateResolvedIncidentComment } =
    useRecoilValue(permissionState);
  const { hideDateTime } = useRecoilValue(incidentsFilterState);
  const [, setPage] = useRecoilState(pageState);
  const { showDrawer } = useDrawer();
  const { locales } = useLocale(user?.language);

  const { editPreparedIncident } = useIncidentInlineAdding(gridRef);
  const flaggedSetter = useGridFlaggedSetter();
  const { defaultValueSetter, commentsSetter, modals, statusSetter } = useGridSetters({
    editTaskPart: editIncidentPart,
    editPreparedTask: editPreparedIncident,
  });

  const columnDefs = useColumnDefs({
    t,
    commentsSetter,
    flaggedSetter,
    statusSetter,
  });

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      hide: false,
      resizable: true,
      editable: cellСanBeEdited,
      minWidth: 60,
      enableCellChangeFlash: true,
      valueSetter: defaultValueSetter,
      comparator: () => 0,
      cellEditorParams: {
        permissions: {
          canUpdateResolvedIncident,
          canUpdateIncident,
          canUpdateAllIncidents,
          canUpdateResolvedIncidentComment,
        },
        currentUserId,
      },
      cellRendererParams: {
        hideDateTime,
      },
      cellStyle: defaultCellStyles,
      suppressMenu: true,
    }),
    [
      defaultValueSetter,
      canUpdateResolvedIncident,
      canUpdateIncident,
      canUpdateAllIncidents,
      canUpdateResolvedIncidentComment,
      currentUserId,
      hideDateTime,
    ],
  );

  const { onSortHandler, columnDefs: updatedColDefs, onDragStopped } = useColumnState({ columnDefs });
  const getLocaleText = useGetLocaleText(t);

  const onChangePage = async (page: number, taskId: number) => {
    setPage(page);
    await showDrawer({ type: Drawer.INCIDENT_EDIT, props: { incidentId: taskId } });
  };

  return (
    <ThemeProvider theme={makeTheme(locales, scale)}>
      <EntitiesGridBox styles={{ '--scale': scale } as CSSProperties}>
        <AgGridReact
          {...gridDefaultSettings}
          containerStyle={CONTAINER_STYLE}
          ref={gridRef}
          rowData={data}
          columnDefs={updatedColDefs}
          rowClassRules={rowClassRules}
          onDragStopped={onDragStopped}
          onSortChanged={onSortHandler}
          defaultColDef={defaultColDef}
          overlayNoRowsTemplate={t('no-results-found')}
          getLocaleText={getLocaleText}
          onRowDataUpdated={({ api }) => {
            api.refreshCells({ force: true });
          }}
          onFirstDataRendered={scrollToRowByParams(onChangePage)}
          onCellEditingStarted={scrollToColumnEdited}
        />
        <Pagination />
        {modals.close}
        {modals.reopen}
      </EntitiesGridBox>
    </ThemeProvider>
  );
};

export default IncidentTable;
